import moment from 'moment';
import pickerMoment from 'react-daterange-picker/dist/moment-range';

export const DATE_SELECTION_MODES = {
  TODAY: 'today',
  HISTORICAL: 'historical',
};

export const PERIOD_SELECTION_TYPES = {
  CUSTOM: 'custom',
  YEAR: 'year',
};

export const CUSTOM_RANGE_OPTION_VALUE = 'custom';

export const DATE_VALIDATION_MESSAGES = {
  MAX_RANGE:
    'Maximum date range allowed is 12 months. Please select a shorter period.',
  HISTORICAL_INCLUDES_TODAY:
    'Historical date range cannot include today. Use Today to view or download current date data.',
  TODAY_COMBINED:
    'Today cannot be combined with historical dates. Select Today or a historical range only.',
  FUTURE_YEAR:
    'Selected year has no historical data available yet.',
};

export const getTodayFormatted = () => moment().format('MM-DD-YYYY');

export const getDefaultTimes = () => ({
  startTime: moment().startOf('day'),
  endTime: moment().endOf('day'),
});

export const formatTimeLabel = time => {
  if (!time) {
    return null;
  }
  return moment(time).format('HH:mm');
};

export const formatPeriodDisplayLabel = ({
  dateSelectionMode,
  dates,
  startDate,
  endDate,
  startTime,
  endTime,
}) => {
  const startTimeLabel = formatTimeLabel(startTime) || '00:00';
  const endTimeLabel = formatTimeLabel(endTime) || '23:59';

  if (dateSelectionMode === DATE_SELECTION_MODES.TODAY) {
    return `${moment().format('D MMM YYYY')} · ${startTimeLabel} – ${endTimeLabel}`;
  }

  const storedDates = toStoredDates(dates, startDate, endDate);
  const start = moment(storedDates.start, 'MM-DD-YYYY').format('D MMM YYYY');
  const end = moment(storedDates.end, 'MM-DD-YYYY').format('D MMM YYYY');

  if (storedDates.start === storedDates.end) {
    return `${start} · ${startTimeLabel} – ${endTimeLabel}`;
  }

  return `${start} ${startTimeLabel} – ${end} ${endTimeLabel}`;
};

export const validateTimeRange = (startDate, endDate, startTime, endTime) => {
  if (!startTime || !endTime) {
    return 'Start time and end time are required.';
  }

  const start = moment(startDate, 'MM-DD-YYYY')
    .hour(moment(startTime).hour())
    .minute(moment(startTime).minute())
    .second(0);
  const end = moment(endDate, 'MM-DD-YYYY')
    .hour(moment(endTime).hour())
    .minute(moment(endTime).minute())
    .second(0);

  if (end.isBefore(start)) {
    return 'End time must be after start time for the selected period.';
  }

  return null;
};

export const getDefaultHistoricalRange = () => {
  const end = moment().subtract(1, 'day');
  const start = end.clone().subtract(6, 'days');
  return {
    startDate: start.format('MM-DD-YYYY'),
    endDate: end.format('MM-DD-YYYY'),
    dates: {
      start: start.format('MM-DD-YYYY'),
      end: end.format('MM-DD-YYYY'),
    },
  };
};

export const getTodayRange = () => {
  const today = getTodayFormatted();
  return {
    startDate: today,
    endDate: today,
    dates: { start: today, end: today },
  };
};

export const rangeIncludesToday = (startDate, endDate) => {
  const today = moment().startOf('day');
  const start = moment(startDate, 'MM-DD-YYYY').startOf('day');
  const end = moment(endDate, 'MM-DD-YYYY').startOf('day');
  return start.isSameOrBefore(today, 'day') && end.isSameOrAfter(today, 'day');
};

export const exceedsMaxDateRange = (startDate, endDate) => {
  const start = moment(startDate, 'MM-DD-YYYY').startOf('day');
  const end = moment(endDate, 'MM-DD-YYYY').startOf('day');
  return end.isAfter(start.clone().add(12, 'months'), 'day');
};

export const isSingleDayToday = (startDate, endDate) => {
  const today = moment().startOf('day');
  const start = moment(startDate, 'MM-DD-YYYY').startOf('day');
  const end = moment(endDate, 'MM-DD-YYYY').startOf('day');
  return start.isSame(today, 'day') && end.isSame(today, 'day');
};

export const validateDateRange = (
  startDate,
  endDate,
  dateSelectionMode,
  periodSelectionType,
) => {
  if (!startDate || !endDate) {
    return 'Date range is required.';
  }

  if (dateSelectionMode === DATE_SELECTION_MODES.TODAY) {
    if (!isSingleDayToday(startDate, endDate)) {
      return DATE_VALIDATION_MESSAGES.TODAY_COMBINED;
    }
    return null;
  }

  if (rangeIncludesToday(startDate, endDate)) {
    return DATE_VALIDATION_MESSAGES.HISTORICAL_INCLUDES_TODAY;
  }

  const isCustomRange = periodSelectionType === PERIOD_SELECTION_TYPES.CUSTOM;
  if (!isCustomRange && exceedsMaxDateRange(startDate, endDate)) {
    return DATE_VALIDATION_MESSAGES.MAX_RANGE;
  }

  return null;
};

export const getFullYearDateRange = year => {
  let start = moment({year, month: 0, day: 1});
  let end = moment({year, month: 11, day: 31});
  const yesterday = moment().subtract(1, 'day').startOf('day');

  if (start.isAfter(yesterday, 'day')) {
    return {error: DATE_VALIDATION_MESSAGES.FUTURE_YEAR};
  }

  if (end.isAfter(yesterday, 'day')) {
    end = yesterday;
  }

  return {
    startDate: start.format('MM-DD-YYYY'),
    endDate: end.format('MM-DD-YYYY'),
    dates: {
      start: start.format('MM-DD-YYYY'),
      end: end.format('MM-DD-YYYY'),
    },
  };
};

export const parseStoredDate = date => {
  if (!date) {
    return null;
  }
  if (moment.isMoment(date)) {
    return date.clone();
  }
  const parsed = moment(date, 'MM-DD-YYYY', true);
  return parsed.isValid() ? parsed : moment(date);
};

export const toStoredDates = (dates, startDate, endDate) => {
  const start = parseStoredDate(dates?.start) || parseStoredDate(startDate);
  const end = parseStoredDate(dates?.end) || parseStoredDate(endDate);
  if (!start?.isValid() || !end?.isValid()) {
    return getDefaultHistoricalRange().dates;
  }
  return {
    start: start.format('MM-DD-YYYY'),
    end: end.format('MM-DD-YYYY'),
  };
};

/** Value shape required by react-daterange-picker (moment-range). */
export const toPickerDateRange = (dates, startDate, endDate) => {
  const start = parseStoredDate(dates?.start) || parseStoredDate(startDate);
  const end = parseStoredDate(dates?.end) || parseStoredDate(endDate);

  if (!start?.isValid() || !end?.isValid()) {
    const fallback = getDefaultHistoricalRange();
    return pickerMoment.range(
      pickerMoment(fallback.startDate, 'MM-DD-YYYY'),
      pickerMoment(fallback.endDate, 'MM-DD-YYYY'),
    );
  }

  return pickerMoment.range(
    pickerMoment(start.clone().startOf('day')),
    pickerMoment(end.clone().startOf('day')),
  );
};

export const MIN_REPORT_YEAR = 2022;

export const getYearOptions = () => {
  const currentYear = moment().year();
  const years = [];
  for (let year = currentYear; year >= MIN_REPORT_YEAR; year -= 1) {
    years.push({ value: year, label: String(year) });
  }
  return years;
};

export const getPeriodSelectOptions = () => [
  { value: CUSTOM_RANGE_OPTION_VALUE, label: 'Custom range' },
  ...getYearOptions(),
];

export const resolvePeriodSelectionType = ({ periodSelectionType }) => {
  if (periodSelectionType) {
    return periodSelectionType;
  }
  return PERIOD_SELECTION_TYPES.YEAR;
};
